import { HttpOptions, HttpHeaders } from "@capacitor/core";
import { RequestMethod, Token, AuthType } from "@/types";
import { isBefore, subMinutes } from "date-fns";
import { v4 as uuidv4 } from "uuid";
import { AuthClient } from "@/services/auth";
import axios, { AxiosRequestConfig } from "axios";
import { localLogger } from "@/utils/localLogger";
import { OfflineException } from "./errors";
import { FabriqStore } from "@/store/fabriq";

export abstract class ApiFactory {
  #serviceUrl: string;
  #baseUrl: string | null = null;
  #token: Token | null;
  #fabriqStore: FabriqStore | undefined;
  #online = false;
  #attempt = 0;
  static #refreshPromise: Promise<void> | null = null;
  #reconnectPromise: Promise<any> | null = null;
  #processingRequests: Map<string, AbortController> = new Map<
    string,
    AbortController
  >();

  #offlineRequests: Array<() => void> = [];

  static get #POST_CONTENT_TYPE(): string {
    return "application/json";
  }

  static get #FORCE_REFRESH_LIMIT(): number {
    return 3;
  }

  static get #MAX_ATTEMPT(): number {
    return 1;
  }

  protected get fabriqStore(): FabriqStore {
    if (this.#fabriqStore) return this.#fabriqStore;
    throw new Error(
      "fabriqStore is not available yet, call .checkToken() first"
    );
  }

  constructor(serviceUrl: string) {
    this.#serviceUrl = serviceUrl;
    this.#token = null;
  }

  async setOnline(online: boolean) {
    if (online === this.#online) return;
    this.#online = online;
    if (this.#online) this.#runOfflineRequest();
    else this.#abortRequests();
  }

  #abortRequests() {
    for (const request of this.#processingRequests.values()) {
      request.abort();
    }
  }

  async checkToken(fabriqStore: FabriqStore) {
    this.#fabriqStore = fabriqStore;
    if (this.#token) {
      try {
        await this.#checkTokenAndStart().finally(() => {
          fabriqStore.endRequest();
        });
      } catch (e) {
        await localLogger.log(
          `An error occured during token check with message: ${e}`
        );
        await AuthClient.reLogin()
          .then((token) => {
            fabriqStore.setToken(token);
            this.setToken(token);
          })
          .catch((e) => {
            console.warn(e);
            fabriqStore.logout();
            fabriqStore.endRequest();
          });
      }
    }
  }

  #checkTokenAndStart(): Promise<void> {
    if (!this.#token) return Promise.resolve();
    const limit = subMinutes(new Date(), ApiFactory.#FORCE_REFRESH_LIMIT);
    if (isBefore(this.#token.expiresAt, limit)) {
      return this.refreshToken();
    }
    return Promise.resolve();
  }

  resetAttempts() {
    this.#attempt = 0;
  }

  async refreshToken(): Promise<void> {
    ApiFactory.#refreshPromise ||= new Promise(async (resolve, reject) => {
      if (!this.#token) return reject(new Error("no token available"));
      switch (this.#token.type) {
        case AuthType.Future:
          try {
            const token = await AuthClient.futureAuthRefreshToken();
            if (!token) throw new Error("no refresh token");
            this.#token = token;
            this.fabriqStore.setToken(this.#token);
            ApiFactory.#refreshPromise = null;
            resolve();
          } catch (e) {
            await this.fabriqStore.logout();
            ApiFactory.#refreshPromise = null;
            reject(new Error("Refresh token error", { cause: e }));
          }
          break;
        case AuthType.Native:
        case AuthType.External:
        case AuthType.Migrated:
        default:
          await localLogger.log(`Deprecated AuthType ${this.#token.type}`);
          reject(
            new Error("AuthType not managed yet", { cause: this.#token.type })
          );
      }
    });
    return ApiFactory.#refreshPromise;
  }

  buildServiceUrl(servicePath: string) {
    if (!this.#baseUrl) return null;
    return `${this.#baseUrl}${servicePath}`;
  }

  applyServiceUrlToBaseUrl(baseUrl: string, servicePath: string) {
    this.#baseUrl = baseUrl;
    this.#serviceUrl = this.buildServiceUrl(servicePath) || this.#serviceUrl;
  }

  #getUrl(path: string): string {
    return `${this.#serviceUrl}${path}`;
  }

  #getOfflineMobileRequestHeader(requestUuid: string): HttpHeaders {
    return {
      "x-mobile-request-uuid": requestUuid,
    };
  }

  getHeaders<T extends AxiosRequestConfig["headers"] | HttpHeaders>(
    headers?: T
  ): T {
    if (!this.#token) return (headers || {}) as T;
    const version = `${this.fabriqStore.version}${
      this.fabriqStore.buildVersion ? `-${this.fabriqStore.buildVersion}` : ""
    }`;
    return {
      ...((headers || {}) as T),
      Authorization:
        this.#token.type === AuthType.Future
          ? `BearerFuture ${this.#token.accessToken}`
          : `Bearer ${this.#token.accessToken}`,
      "x-mobile-app-version": version,
    } as T;
  }

  async getFreshToken() {
    await this.#checkTokenAndStart();
    return this.#token;
  }

  setToken(token: Token | null) {
    this.#token = token;
  }

  runOfflineRequests() {
    while (this.#online && this.#offlineRequests.length) {
      this.#runOfflineRequest();
    }
  }

  fetch(url: string, options?: any) {
    const requestInit: RequestInit = {
      method: "GET",
      ...(options || {}),
      headers: {
        ...this.getHeaders(options?.headers),
        "Content-Type": "application/json",
      },
    };
    return fetch(url, requestInit).then((res) => res.json());
  }

  #runOfflineRequest() {
    const request = this.#offlineRequests.shift();
    if (!request) return;
    return request();
  }

  #addOffline<T>(
    method: RequestMethod,
    url: string,
    params?: any
  ): Promise<T | void> {
    return new Promise((resolve, reject) => {
      const executor = async () => {
        try {
          if (method === RequestMethod.Post) {
            const result = await this.post<T>(url, params);
            return resolve(result);
          }
          if (method === RequestMethod.Patch) {
            const result = await this.patch<T>(url, params);
            return resolve(result);
          }
          if (method === RequestMethod.Delete) {
            const result = await this.delete<T>(url, params);
            return resolve(result);
          }
          const result = await this.get<T>(url, params);
          resolve(result);
        } catch (e) {
          reject(e);
        }
      };
      this.#offlineRequests.push(executor);
    });
  }

  sendFile(url: string, params: any, request?: { requestUuid: string }) {
    const offlineHeaders = request?.requestUuid
      ? this.#getOfflineMobileRequestHeader(request.requestUuid)
      : {};

    return axios
      .post(this.#getUrl(url), params, {
        headers: this.getHeaders(offlineHeaders),
      })
      .then((r: any) => r.data);
  }

  getRequestOptions(url: string) {
    const requestOptions: HttpOptions = {
      url,
      method: "GET",
      headers: this.getHeaders(),
    };
    return requestOptions;
  }

  async get<T>(url: string, params?: any, notBased = false): Promise<T | void> {
    console.log("GET Request for", url);
    if (!this.#online) {
      return this.#addOffline(RequestMethod.Get, url, params);
    }
    await this.#checkTokenAndStart();
    const requestOptions: HttpOptions = {
      url: notBased ? url : this.#getUrl(url),
      method: "GET",
      headers: notBased ? undefined : this.getHeaders(),
      params,
    };
    return this.sendRequest<T>(requestOptions);
  }

  async getAxios<T>(
    url: string,
    params?: any,
    notBased = false
  ): Promise<T | void> {
    if (!this.#online) {
      return this.#addOffline(RequestMethod.Get, url, params);
    }
    await this.#checkTokenAndStart();
    const requestOptions: HttpOptions = {
      url: notBased ? url : this.#getUrl(url),
      method: "GET",
      headers: notBased ? undefined : this.getHeaders(),
      params,
    };
    return this.sendAxiosRequest<T>(requestOptions);
  }

  async post<T>(
    url: string,
    data?: any,
    capacitor?: boolean,
    request?: { requestUuid: string }
  ): Promise<T | void> {
    console.log("POST Request for", url);
    if (!this.#online) {
      if (!request) {
        return this.#addOffline<T>(RequestMethod.Post, url, data);
      } else {
        // The request is coming from FabriqIdbStorage
        throw new OfflineException();
      }
    }
    await this.#checkTokenAndStart();
    let headers: HttpHeaders = {
      "Content-Type": ApiFactory.#POST_CONTENT_TYPE,
    };
    if (request?.requestUuid) {
      const offlineHeaders = this.#getOfflineMobileRequestHeader(
        request.requestUuid
      );
      headers = { ...headers, ...offlineHeaders };
    }

    const requestOptions: HttpOptions = {
      url: this.#getUrl(url),
      method: "POST",
      headers: this.getHeaders(headers),
      data,
    };
    if (capacitor) return this.sendRequest(requestOptions);
    return this.sendAxiosRequest<T>(requestOptions);
  }

  async put<T>(
    url: string,
    data?: any,
    request?: { requestUuid: string }
  ): Promise<T | void> {
    console.log("PUT Request for", url);
    if (!this.#online) {
      if (!request) {
        return this.#addOffline(RequestMethod.Put, url, data);
      } else {
        // The request is coming from FabriqIdbStorage
        throw new OfflineException();
      }
    }
    await this.#checkTokenAndStart();
    let headers: HttpHeaders = {
      "Content-Type": ApiFactory.#POST_CONTENT_TYPE,
    };
    if (request?.requestUuid) {
      const offlineHeaders = this.#getOfflineMobileRequestHeader(
        request.requestUuid
      );
      headers = { ...headers, ...offlineHeaders };
    }
    const requestOptions: HttpOptions = {
      url: this.#getUrl(url),
      method: "PUT",
      headers: this.getHeaders(headers),
      data,
    };
    return this.sendAxiosRequest<T>(requestOptions);
  }

  async patch<T>(
    url: string,
    data?: any,
    request?: { requestUuid: string }
  ): Promise<T | void> {
    console.log("PATH Request for", url);
    if (!this.#online) {
      if (!request) {
        return this.#addOffline<T>(RequestMethod.Patch, url, data);
      } else {
        // The request is coming from FabriqIdbStorage
        throw new OfflineException();
      }
    }

    await this.#checkTokenAndStart();
    const offlineHeaders = request?.requestUuid
      ? this.#getOfflineMobileRequestHeader(request.requestUuid)
      : {};
    const requestOptions: HttpOptions = {
      url: this.#getUrl(url),
      method: "PATCH",
      headers: this.getHeaders(offlineHeaders),
      data,
    };
    return this.sendAxiosRequest<T>(requestOptions);
  }

  async delete<T>(
    url: string,
    params?: any,
    data?: any,
    request?: { requestUuid: string }
  ): Promise<T | void> {
    if (!this.#online) {
      if (!request) {
        return this.#addOffline<T>(RequestMethod.Delete, url, params);
      } else {
        // The request is coming from FabriqIdbStorage
        throw new OfflineException();
      }
    }

    await this.#checkTokenAndStart();
    const offlineHeaders = request?.requestUuid
      ? this.#getOfflineMobileRequestHeader(request.requestUuid)
      : {};
    const requestOptions: HttpOptions = {
      url: this.#getUrl(url),
      method: "DELETE",
      headers: this.getHeaders(offlineHeaders),
      params,
      data,
    };
    return this.sendAxiosRequest<T>(requestOptions);
  }

  async reconnectAttempt(): Promise<any> {
    if (this.#attempt > ApiFactory.#MAX_ATTEMPT) {
      this.fabriqStore.$reset();
      return Promise.reject("Disconnected");
    }
    if (!this.#reconnectPromise) {
      this.#attempt++;
      this.#reconnectPromise = AuthClient.reLogin()
        .then((token) => {
          this.fabriqStore.setToken(token);
          this.setToken(token);
        })
        .catch((e) => {
          console.error("RECONNECT ARRRF", e);
        })
        .finally(() => {
          this.#reconnectPromise = null;
        });
    }
    return this.#reconnectPromise;
  }

  abstract sendRequest<T>(
    requestOptions: HttpOptions | AxiosRequestConfig
  ): Promise<T | void>;

  async sendAxiosRequest<T>(
    requestOptions: AxiosRequestConfig
  ): Promise<T | void> {
    console.log("SEND Axios REQUEST Request for", requestOptions.url);
    const fabriqStore = this.fabriqStore;
    fabriqStore.startRequest();
    const identifier = uuidv4();
    try {
      const controller = new AbortController();
      this.#processingRequests.set(identifier, controller);
      const response: any = await axios({
        ...requestOptions,
        signal: controller.signal,
      });
      this.#processingRequests.delete(identifier);
      this.resetAttempts();
      fabriqStore.endRequest();
      return response.data;
    } catch (e: any) {
      console.error("Error in sendAxiosRequest", e);
      fabriqStore.endRequest();
      this.#processingRequests.delete(identifier);
      if (e && e.response && e.response.status === 401) {
        return this.refreshToken()
          .then(() => {
            this.resetAttempts();
            return this.sendAxiosRequest<T>({
              ...requestOptions,
              headers: this.getHeaders(requestOptions.headers),
            });
          })
          .catch((e) => {
            console.warn(e);
            return this.reconnectAttempt()
              .then(() => {
                this.resetAttempts();
                return this.sendAxiosRequest<T>({
                  ...requestOptions,
                  headers: this.getHeaders(requestOptions.headers),
                });
              })
              .catch((e) => {
                console.error("Error", e);
                fabriqStore.logout();
              });
          });
      }
      if (
        e?.response?.status === 500 ||
        e?.response?.status === 403 ||
        e?.response?.status === 409
      )
        throw new Error("500", {
          cause: e,
        });

      throw e;
    }
  }
}
